import React, { useEffect, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPenToSquare, faPlus } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import List from "../../../components/List/List"
import { convertToLocalMoney } from "../../../Objects/Generics/Helps"
import { EAccountPlansType } from "../../../Enum/EAccountPlansType"
import { AdminPlan } from "../../../Objects/AdminPlan/AdminPlan"
import { AdminPlanPeriod } from "../../../Objects/AdminPlan/AdminPlanPeriod"
import { useAdminPlan } from "../../../hooks/AdminDashboard/useAdminPlan"
import { AdminPlanFormPopup } from "../AdminPlanFormPopup"
import { AdminPlanPeriodPopup } from "../AdminPlanPeriodPopup"
import { AdminListCard } from "../components/AdminListCard"

const TOTAL_ACCOUNT_PLAN_TYPES = Object.values(EAccountPlansType).filter((value) => typeof value === "number").length

const PLAN_FILTERS = [
  "admin_dashboard.plans_and_coupons.plan_filter_active",
  "admin_dashboard.plans_and_coupons.plan_filter_inactive",
  "admin_dashboard.plans_and_coupons.plan_filter_all",
] as const

export const PlansSection: React.FC = () => {
  const { t } = useTranslation("frontend")

  const { handleGetAllPlans, handleCreatePlan, handleUpdatePlan, handleUpdatePlanPeriod, handleCreatePlanPeriod } =
    useAdminPlan()

  const [plans, setPlans] = useState<AdminPlan[]>([])

  const [isPlanFormOpen, setIsPlanFormOpen] = useState(false)

  const [editingPlan, setEditingPlan] = useState<AdminPlan | null>(null)

  const [periodPopup, setPeriodPopup] = useState<{ plan: AdminPlan; period: AdminPlanPeriod | null } | null>(null)

  const refreshPlans = () => handleGetAllPlans(setPlans)

  useEffect(() => {
    refreshPlans()
  }, [])

  const dropdownFilters = PLAN_FILTERS.map((key) => t(key))

  const handleAddPlan = () => {
    setEditingPlan(null)
    setIsPlanFormOpen(true)
  }

  const handleEditPlan = (id: number) => {
    const plan = plans.find((item) => item.id === id)

    if (!plan) return

    setEditingPlan(plan)
    setIsPlanFormOpen(true)
  }

  return (
    <div className="w-full overflow-auto bg-white p-1 md:p-0">
      <div className="p-2">
        <List
          messageCount={t("admin_dashboard.plans_and_coupons.plans_count")}
          messageEmpty={t("admin_dashboard.plans_and_coupons.plans_empty")}
          nameFindSearch={t("admin_dashboard.plans_and_coupons.plans_search")}
          nameDropdown={dropdownFilters}
          handleAddButton={plans.length < TOTAL_ACCOUNT_PLAN_TYPES ? handleAddPlan : undefined}
          handleRefreshButton={refreshPlans}
          handleOnClick={handleEditPlan}
          data={plans.map((plan, index) => {
            const status = plan.isActive
              ? t("admin_dashboard.plans_and_coupons.plan_filter_active")
              : t("admin_dashboard.plans_and_coupons.plan_filter_inactive")

            const typeLabel = plan.eAccountPlansType.toEAccountPlansTypeToString()

            return {
              id: plan.id,
              index,
              title: plan.name,
              linkImage: "",
              isDisable: !plan.isActive,
              dataFilter: [plan.name, typeLabel, status],
              children: (
                <AdminListCard
                  title={plan.name}
                  subtitle={plan.description || typeLabel}
                  badge={status}
                  badgeClass={plan.isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"}
                  cardClass={
                    plan.isActive
                      ? "rounded-xl border-2 border-orange-200 bg-orange-50/30 p-3"
                      : "rounded-xl border-2 border-slate-200 bg-slate-50 p-3"
                  }
                >
                  <div className="grid grid-cols-1 gap-2">
                    {plan.periods.map((period) => (
                      <div
                        key={period.id}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 bg-white px-3 py-2"
                      >
                        <div className="min-w-0">
                          <p className="text-[10px] font-black uppercase tracking-wide text-slate-500">
                            {period.eBillingPeriodType.toEBillingPeriodTypeToString()}
                          </p>

                          <p className="mt-0.5 text-sm font-bold text-slate-800">
                            {convertToLocalMoney(period.price)}
                          </p>
                        </div>

                        <div className="flex items-center gap-2">
                          <span
                            className={`rounded-full px-2.5 py-1 text-[11px] font-black uppercase ${
                              period.isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {period.isActive
                              ? t("admin_dashboard.plans_and_coupons.period_active")
                              : t("admin_dashboard.plans_and_coupons.period_inactive")}
                          </span>

                          <button
                            className="inline-flex min-h-[30px] items-center justify-center gap-1 rounded-lg border border-blue-500 bg-blue-600 px-3 py-1 text-xs font-bold text-white transition-colors hover:bg-blue-700"
                            onClick={(event) => {
                              event.stopPropagation()
                              setPeriodPopup({ plan, period })
                            }}
                            data-interaction-description-key="interactionDescriptions:pages.adminDashboard.sections.plansSection.action.editPeriod"
                          >
                            <FontAwesomeIcon icon={faPenToSquare} />
                            <span>{t("admin_dashboard.plans_and_coupons.period_edit_action")}</span>
                          </button>
                        </div>
                      </div>
                    ))}

                    {plan.periods.length < 3 && (
                      <button
                        className="inline-flex min-h-[34px] items-center justify-center gap-1 rounded-lg border border-dashed border-orange-300 bg-white px-3 py-1.5 text-xs font-bold text-orange-700 transition-colors hover:bg-orange-50"
                        onClick={(event) => {
                          event.stopPropagation()
                          setPeriodPopup({ plan, period: null })
                        }}
                        data-interaction-description-key="interactionDescriptions:pages.adminDashboard.sections.plansSection.action.addPeriod"
                      >
                        <FontAwesomeIcon icon={faPlus} />
                        <span>{t("admin_dashboard.plans_and_coupons.period_add_action")}</span>
                      </button>
                    )}
                  </div>

                  <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-3">
                    <button
                      className="inline-flex min-h-[34px] items-center justify-center gap-1 rounded-lg border border-orange-400 bg-orange-500 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-orange-600"
                      onClick={(event) => {
                        event.stopPropagation()
                        handleEditPlan(plan.id)
                      }}
                      data-interaction-description-key="interactionDescriptions:pages.adminDashboard.sections.plansSection.action.editPlan"
                    >
                      <FontAwesomeIcon icon={faPenToSquare} />
                      <span>{t("admin_dashboard.plans_and_coupons.plan_edit_action")}</span>
                    </button>
                  </div>
                </AdminListCard>
              ),
            }
          })}
        />
      </div>

      {isPlanFormOpen && (
        <AdminPlanFormPopup
          plan={editingPlan}
          existingPlans={plans}
          onClose={() => setIsPlanFormOpen(false)}
          onCreate={(request) => {
            handleCreatePlan(request, () => {
              setIsPlanFormOpen(false)
              refreshPlans()
            })
          }}
          onUpdate={(planId, request) => {
            handleUpdatePlan(planId, request, () => {
              setIsPlanFormOpen(false)
              refreshPlans()
            })
          }}
        />
      )}

      {periodPopup && (
        <AdminPlanPeriodPopup
          plan={periodPopup.plan}
          period={periodPopup.period}
          onClose={() => setPeriodPopup(null)}
          onCreate={(planId, request) => {
            handleCreatePlanPeriod(planId, request, () => {
              setPeriodPopup(null)
              refreshPlans()
            })
          }}
          onUpdate={(planPeriodId, request) => {
            handleUpdatePlanPeriod(planPeriodId, request, () => {
              setPeriodPopup(null)
              refreshPlans()
            })
          }}
        />
      )}
    </div>
  )
}
